import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import PieChart from "examples/Charts/PieChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import {
  DeleteAppReviewFeedbackCall,
  GetAppReviewFeedbackCall,
} from "../../redux/api/appReviewFeedback.api";

const emptySummary = {
  thumbs_up: 0,
  thumbs_down: 0,
  total: 0,
  by_platform: [],
};

function platformLabel(key) {
  const k = String(key || "").toLowerCase();
  if (k === "ios") return "iOS";
  if (k === "android") return "Android";
  return "Unknown";
}

/** ISO 3166-1 alpha-2 → flag emoji (same pattern as demo requests). */
function isoToFlag(iso) {
  if (!iso || typeof iso !== "string") return "";
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const base = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + code.charCodeAt(0) - a,
    base + code.charCodeAt(1) - a,
  );
}

function userDisplayName(user) {
  const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return full || user?.email || "User";
}

function userInitials(user) {
  const first = user?.first_name?.charAt(0) || "";
  const last = user?.last_name?.charAt(0) || "";
  const combo = `${first}${last}`.toUpperCase();
  if (combo) return combo;
  if (user?.email) return user.email.charAt(0).toUpperCase();
  return "?";
}

function FeedbackUserCell({ user, userId }) {
  const name = userDisplayName(user);
  const email = user?.email || (userId ? String(userId).slice(0, 8) : "—");
  const country = (user?.country || "").trim().toUpperCase();
  const flag = isoToFlag(country);
  const countryTitle = country || "";

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      {flag ? (
        <Box
          component="span"
          title={countryTitle}
          aria-label={countryTitle}
          sx={{
            fontSize: "1.75rem",
            lineHeight: 1,
            flexShrink: 0,
            width: 36,
            height: 36,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {flag}
        </Box>
      ) : (
        <Avatar sx={{ width: 36, height: 36, flexShrink: 0 }}>
          {userInitials(user)}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          title={name}
          sx={{ minWidth: 0, lineHeight: 1.3 }}
        >
          {name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          display="block"
          title={email}
        >
          {email}
        </Typography>
        {country ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
            title={countryTitle}
            sx={{ mt: 0.15 }}
          >
            {country}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function FeedbackMobileCard({ row, onDelete }) {
  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: "divider",
      }}
    >
      <Stack spacing={1.25}>
        <FeedbackUserCell user={row.user} userId={row.user_id} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Chip
            size="small"
            color={row.sentiment === "up" ? "success" : "error"}
            icon={row.sentiment === "up" ? <ThumbUpIcon /> : <ThumbDownIcon />}
            label={row.sentiment === "up" ? "Up" : "Down"}
          />
          <Typography variant="caption" color="text.secondary">
            {moment(row.created_at).format("MMM D, HH:mm")}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            label={row.is_subscriber ? "Subscriber" : "Free"}
          />
          <Chip
            size="small"
            variant="outlined"
            label={row.platform || "—"}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${row.session_count ?? "—"} sessions`}
          />
        </Stack>

        <Box display="flex" justifyContent="flex-end">
          <IconButton
            size="small"
            color="error"
            aria-label="Delete feedback"
            onClick={() => onDelete(row.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
    </Card>
  );
}

export default function AppFeedbackPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await GetAppReviewFeedbackCall({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });
    setRows(res.rows || []);
    setTotal(typeof res.total === "number" ? res.total : res.rows?.length || 0);
    setSummary(res.summary || emptySummary);
    if (!res.ok) {
      setError(res.error || "Could not load app feedback");
    }
    setLoading(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const onDelete = async (id) => {
    await DeleteAppReviewFeedbackCall(id);
    void load();
  };

  const upCount = Number(summary?.thumbs_up) || 0;
  const downCount = Number(summary?.thumbs_down) || 0;
  // If API/client summary is still empty, count the visible table rows.
  const rowDerived = useMemo(() => {
    let up = 0;
    let down = 0;
    const map = {};
    for (const row of rows) {
      const sentiment = String(row?.sentiment || "")
        .trim()
        .toLowerCase();
      if (sentiment === "up") up += 1;
      else if (sentiment === "down") down += 1;
      const key =
        String(row?.platform || "unknown")
          .trim()
          .toLowerCase() || "unknown";
      if (!map[key]) map[key] = { platform: key, up: 0, down: 0, total: 0 };
      if (sentiment === "up") map[key].up += 1;
      else if (sentiment === "down") map[key].down += 1;
      map[key].total += 1;
    }
    return {
      thumbs_up: up,
      thumbs_down: down,
      total: up + down,
      by_platform: Object.values(map),
    };
  }, [rows]);

  const displayUp = upCount > 0 || downCount > 0 ? upCount : rowDerived.thumbs_up;
  const displayDown =
    upCount > 0 || downCount > 0 ? downCount : rowDerived.thumbs_down;
  const allTotal = displayUp + displayDown;
  const displayPlatforms =
    Array.isArray(summary?.by_platform) && summary.by_platform.length > 0
      ? summary.by_platform
      : rowDerived.by_platform;

  const sentimentPie = useMemo(
    () => ({
      labels: ["Thumbs up", "Thumbs down"],
      datasets: {
        label: "Feedback",
        backgroundColors: ["success", "error"],
        data: [displayUp, displayDown],
      },
    }),
    [displayUp, displayDown],
  );

  const platformBar = useMemo(() => {
    const platforms = Array.isArray(displayPlatforms) ? displayPlatforms : [];
    const labels =
      platforms.length > 0
        ? platforms.map((p) => platformLabel(p.platform))
        : ["iOS", "Android"];
    const totals =
      platforms.length > 0
        ? platforms.map((p) => Number(p.total) || 0)
        : [0, 0];

    return {
      labels,
      datasets: {
        label: "Total feedback",
        data: totals,
      },
    };
  }, [displayPlatforms]);

  const platformDescription = useMemo(() => {
    const platforms = Array.isArray(displayPlatforms) ? displayPlatforms : [];
    if (!platforms.length) return "No platform breakdown yet";
    return platforms
      .map(
        (p) =>
          `${platformLabel(p.platform)}: ${p.up || 0}↑ / ${p.down || 0}↓`,
      )
      .join(isMobile ? "\n" : " · ");
  }, [displayPlatforms, isMobile]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={{ xs: 2, md: 3 }}
        px={{ xs: 1.5, sm: 2, md: 3 }}
        sx={{ maxWidth: "100%", overflowX: "hidden" }}
      >
        <Stack spacing={0.75} mb={{ xs: 2, md: 3 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight={800}
            sx={{ lineHeight: 1.25 }}
          >
            App review feedback
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 720 }}
          >
            Thumbs up / down from the in-app review prompt. Totals and charts
            cover all responses.
          </Typography>
        </Stack>

        {error ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderColor: "error.light",
              bgcolor: "error.lighter",
            }}
          >
            <Typography variant="body2" color="error.main" fontWeight={600}>
              {error}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              If this mentions a missing table, run{" "}
              <code>yarn db:migrate</code> on the API (or{" "}
              <code>yarn release-prod-migrate</code> from mic-backend).
            </Typography>
          </Paper>
        ) : null}

        <Stack
          direction="row"
          spacing={1}
          mb={2}
          flexWrap="wrap"
          useFlexGap
          sx={{
            "& .MuiChip-root": {
              maxWidth: "100%",
            },
          }}
        >
          <Chip
            icon={<ThumbUpIcon />}
            label={`Up: ${displayUp}`}
            color="success"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          <Chip
            icon={<ThumbDownIcon />}
            label={`Down: ${displayDown}`}
            color="error"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          <Chip
            label={`Total: ${allTotal}`}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
        </Stack>

        <Grid container spacing={{ xs: 3, md: 2.5 }} mb={3} mt={{ xs: 1, md: 2 }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ pt: { xs: 2, md: 0 } }}>
              <PieChart
                icon={{ color: "success", component: "thumb_up" }}
                title="Sentiment (all)"
                description={`${allTotal} total responses`}
                chart={sentimentPie}
                height={isMobile ? "14rem" : "18rem"}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ pt: { xs: 2.5, md: 0 } }}>
              <ReportsBarChart
                color="info"
                title="By platform (all)"
                description={
                  <Box
                    component="span"
                    sx={{
                      whiteSpace: isMobile ? "pre-line" : "normal",
                      display: "block",
                    }}
                  >
                    {platformDescription}
                  </Box>
                }
                date="All time"
                chart={platformBar}
              />
            </Box>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box py={6} display="flex" justifyContent="center">
              <CircularProgress size={32} />
            </Box>
          ) : isTabletDown ? (
            <>
              <Stack spacing={1.25} sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                {rows.length === 0 ? (
                  <Typography color="text.secondary" py={2} px={1}>
                    No feedback yet.
                  </Typography>
                ) : (
                  rows.map((row) => (
                    <FeedbackMobileCard
                      key={row.id}
                      row={row}
                      onDelete={onDelete}
                    />
                  ))
                )}
              </Stack>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={isMobile ? "Rows" : "Rows per page:"}
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  ".MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    justifyContent: isMobile ? "center" : "flex-end",
                    px: 1,
                    gap: 0.5,
                    minHeight: 56,
                  },
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                    {
                      margin: 0,
                    },
                }}
              />
            </>
          ) : (
            <>
              <TableContainer sx={{ maxWidth: "100%" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Sentiment</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Subscriber</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Sessions</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary" py={2}>
                            No feedback yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ maxWidth: 320, py: 1.5 }}>
                            <FeedbackUserCell
                              user={row.user}
                              userId={row.user_id}
                            />
                          </TableCell>
                          <TableCell>
                            {row.sentiment === "up" ? (
                              <Chip
                                size="small"
                                color="success"
                                icon={<ThumbUpIcon />}
                                label="Up"
                              />
                            ) : (
                              <Chip
                                size="small"
                                color="error"
                                icon={<ThumbDownIcon />}
                                label="Down"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {moment(row.created_at).format(
                              "MMM D, YYYY HH:mm",
                            )}
                          </TableCell>
                          <TableCell>
                            {row.is_subscriber ? "Yes" : "No"}
                          </TableCell>
                          <TableCell>{row.platform || "—"}</TableCell>
                          <TableCell>{row.session_count ?? "—"}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDelete(row.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Paper>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
